import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BackendTokenAPI } from '../../../shared/api/backend'
import * as _ from 'lodash'
import { RootState } from '../../../store/store'
import {
  config,
  Filters,
  getFilters,
  GroupedLogs,
  logHistory,
  MESSAGE,
  resolvedRequest,
  retryRequest,
  SelectedGroupedLogs,
  SelectedGroupFfilter,
} from '../Utils'

export const api = {
  async getGroupByLogs(params: Filters) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    // Request body according to the expected format
    const requestBody = {
      status: params.status,
      start: 0,
      limit: config.ITEMS_PER_PAGE,
      order_by: params.orderBy || 'desc',
    } as any

    // optional parameters
    if (params.type && params.search_term) {
      requestBody.type = params.type
      requestBody.search_term = params.search_term
    }

    // Pagination token
    if (params.start) {
      requestBody.start = params.start
    }

    if (!_.isEmpty(params.range)) {
      requestBody.range = params.range
    }

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    if (params.countries?.length) {
      requestBody.countrys = params.countries
    }

    if (params.integration_partner?.length) {
      requestBody.integration_partner = params.integration_partner
    }

    if (!_.isEmpty(params.customer_name)) {
      requestBody.customer_name = params.customer_name
    }

    console.log('requestBody', requestBody)

    const response = await fetch(config.API_PATH + '/logs', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed fetching the list: ${response.body ?? response.status}`)
    }

    const data = await response.json()
    console.log('data....', data)

    return {
      logs: data.data || [],
      total: data.count || 0,
    }
  },
  async getSelectedGroupByLogs(params: SelectedGroupFfilter) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    // Request body according to the expected format
    const requestBody = {
      status: params.status,
      start: 0,
      limit: config.ITEMS_PER_SCROLL,
      purchase_order_number: params.purchase_order_number || '',
      status_code: params.status_code || '',
      activity_type: params.activity_type || '',
    } as any

    // optional parameters
    if (params.type && params.type !== 'purchase_order_number' && params.search_term) {
      requestBody.type = params.type
      requestBody.search_term = params.search_term
    }

    // Pagination token
    if (params.start) {
      requestBody.start = params.start
    }

    if (!_.isEmpty(params.range)) {
      requestBody.range = params.range
    }

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    if (params.org_id !== undefined && params.org_id !== null && String(params.org_id).length) {
      requestBody.org_id = String(params.org_id)
    }

    if (params.countries?.length) {
      requestBody.countrys = params.countries
    }

    if (params.integration_partner?.length) {
      requestBody.integration_partner = params.integration_partner
    }

    if (!_.isEmpty(params.customer_name)) {
      requestBody.customer_name = params.customer_name
    }

    console.log('requestBody', requestBody)

    const response = await fetch(config.API_PATH + '/logs/details', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed fetching the list: ${response.body ?? response.status}`)
    }

    const data = await response.json()
    console.log('data....', data)

    return {
      selectedLogs: data.data || [],
      total: 0,
    }
  },
  async resolveRequest(params: resolvedRequest) {
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      params.isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    const headers = { ...authHeader }

    if (params.email) {
      headers.email = params.email
    }

    const request = _.omit(params, ['isAdmin', 'email'])

    const response = await fetch(config.API_PATH, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      throw new Error(`Failed changing the request to resolve: ${response.body ?? response.status}`)
    }

    return response.json()
  },
  async retryRequest(params: retryRequest) {
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      params.isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    const requestBody: any = {}

    // Retry a specific set of logs by id ...
    if (params.log_ids?.length) {
      requestBody.log_ids = params.log_ids
      requestBody.purchase_order_number = params.purchase_order_number || ''
      requestBody.new_purchase_order_number = params.new_purchase_order_number || ''
    } else {
      // ... or a group of logs matching the condition below
      if (params.status) {
        requestBody.status = params.status
      }
      requestBody.purchase_order_number = params.purchase_order_number || ''
      requestBody.new_purchase_order_number = params.new_purchase_order_number || ''
      requestBody.status_code = params.status_code ?? ''
      requestBody.activity_type = params.activity_type || ''
      requestBody.org_id = params.org_id || ''
    }

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    const response = await fetch(config.API_PATH + '/retry', {
      method: 'PUT',
      headers: authHeader,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed to retry the request: ${response.body ?? response.status}`)
    }

    return response.json()
  },
  async getLogHistory(params: logHistory) {
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      params.isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    // Request body according to the expected format
    const requestBody = {
      log_id: params.log_id,
      start: params.start || 0,
      limit: params.limit || 5,
    }

    const response = await fetch(config.API_PATH + '/history', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed fetching the list: ${response.body ?? response.status}`)
    }

    const data = await response.json()
    console.log('data....', data)

    return {
      history: data,
    }
  },
  async getFilter(params: getFilters) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    // Request body according to the expected format
    const requestBody = {
      type: params.type,
    } as any

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    const response = await fetch(config.API_PATH + '/filter', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed fetching the list: ${response.body ?? response.status}`)
    }

    const data = await response.json()
    console.log('data....', data)

    return {
      filter: data.data,
    }
  },
  async getDownloadLogs(params: Filters) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    const requestBody: any = {
      status: params.status,
    }

    if (params.type && params.search_term) {
      requestBody.type = params.type
      requestBody.search_term = params.search_term
    }

    if (!_.isEmpty(params.range)) {
      requestBody.range = params.range
    }

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    if (params.countries?.length) {
      requestBody.countrys = params.countries
    }

    if (params.integration_partner?.length) {
      requestBody.integration_partner = params.integration_partner
    }

    if (!_.isEmpty(params.customer_name)) {
      requestBody.customer_name = params.customer_name
    }

    const response = await fetch(config.API_PATH + '/download', {
      method: 'POST',
      headers: {
        ...authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`)
    }

    const base64Data = await response.text()

    const contentType =
      response.headers.get('Content-Type') || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length).fill(null).map((_, i) => byteCharacters.charCodeAt(i))
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: contentType })
    return blob
  },

  /**
   * Request async export via email
   * This sends a request to the backend to generate an export file
   * and send it to the specified email address
   */
  async requestAsyncExport(params: Filters, email: string) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    const requestBody: any = {
      status: params.status,
      email: email, // Add email to request body
    }

    if (params.type && params.search_term) {
      requestBody.type = params.type
      requestBody.search_term = params.search_term
    }

    if (!_.isEmpty(params.range)) {
      requestBody.range = params.range
    }

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    if (params.countries?.length) {
      requestBody.countrys = params.countries
    }

    if (params.integration_partner?.length) {
      requestBody.integration_partner = params.integration_partner
    }

    if (!_.isEmpty(params.customer_name)) {
      requestBody.customer_name = params.customer_name
    }

    const response = await fetch(config.API_PATH + '/download', {
      method: 'POST',
      headers: {
        ...authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Export request failed: ${response.status}`)
    }

    const result = await response.json()
    return result
  },
}

// Thunks for Redux actions
export const getGroupByLogs = createAsyncThunk('customLogs/getGroupByLogs', async (params: Filters) => {
  return await api.getGroupByLogs(params)
})

export const getSelectedGroupByLogs = createAsyncThunk(
  'customLogs/getSelectedGroupByLogs',
  async (params: SelectedGroupFfilter) => {
    return await api.getSelectedGroupByLogs(params)
  },
)

export const resolveRequestThunk = createAsyncThunk(
  'requests/resolve',
  async (request: resolvedRequest, { dispatch, getState }) => {
    await api.resolveRequest(request)

    const state = getState() as RootState
    const { filters, pagination } = state.customLogs

    return dispatch(
      getGroupByLogs({
        ...filters,
        limit: pagination.limit,
      }),
    )
  },
)

export const retryRequestThunk = createAsyncThunk(
  'requests/retry',
  async (request: retryRequest, { dispatch, getState }) => {
    await api.retryRequest(request)

    const state = getState() as RootState
    const { filters, pagination } = state.customLogs

    await dispatch(
      getGroupByLogs({
        ...filters,
        limit: pagination.limit,
      }),
    )
  },
)

export const getLogHistory = createAsyncThunk('requests/getLogHistory', async (params: logHistory) => {
  return api.getLogHistory({ ...params })
})

export const getCountryFiltersDetails = createAsyncThunk(
  'requests/getCountryFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'country' })
  },
)

export const getCustomerPortalNameFiltersDetails = createAsyncThunk(
  'requests/getCustomerPortalNameFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'integration_partner' })
  },
)

export const getCustomerNameFiltersDetails = createAsyncThunk(
  'requests/getCustomerNameFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'customer_name' })
  },
)

// Initial state
const initialState = {
  logs: [] as GroupedLogs[],
  loading: false,
  error: null as string | null,
  totalPage: 1,
  totalRecord: 0,
  filters: {
    status: MESSAGE.ERROR,
    type: '',
    search_term: '',
    start: 0,
    orderBy: 'desc',
  } as Filters,
  pagination: {
    currentPage: 1,
    limit: config.ITEMS_PER_PAGE,
    hasMore: true,
  },
  resolvedLoading: false,
  resolvedSuccess: false,
  retryLoading: false,
  retrySuccess: false,
  countries: [],
  customer_portal_names: [],
  history: [],
  loadingHistory: false,
  customer_names: [],
  selectedGroupLogs: [] as SelectedGroupedLogs[],
  loadingSelectedGroupLogs: false,
  loadingLogs: false,
  resolvedSelected: 0,
  expandedRowKeys: [],
}

const customLogSlice = createSlice({
  name: 'customLogs',
  initialState,
  reducers: {
    updateFilters(state, action) {
      console.log('state.filters', action.payload)
      state.filters = { ...state.filters, ...action.payload }
      console.log('state.filters', state.filters)
    },
    setPage(state, action) {
      if (action.payload === 'next') {
        const start = 10 * state.pagination.currentPage + 1

        state.filters.start = start === 1 ? 0 : start

        state.pagination.currentPage += 1
      } else if (action.payload === 'prev' && state.pagination.currentPage > 1) {
        state.pagination.currentPage -= 1

        state.filters.start = 10 * (state.pagination.currentPage - 1) + 1
      }
    },
    clearSearch(state) {
      state.filters.search_term = ''
      state.filters.type = ''
      state.filters.start = 0
      state.filters.range = {}
      state.pagination.currentPage = 1
      state.filters.customer_name = []
      state.filters.countries = []
      state.filters.integration_partner = []
      state.filters.status = 'Error'
    },
    updatePagination(state, action) {
      // Reset pagination tokens and history when filters change
      state.pagination.currentPage = 1
      state.filters.start = 0
    },
    setLogStatus(state, action) {
      state.resolvedSuccess = false
      state.retrySuccess = false
    },
    clearSelectedLogGroups(state, action) {
      state.selectedGroupLogs = []
    },
    setResolvedSelected(state, action) {
      state.resolvedSelected = action.payload
    },
    setExpandedRowKeys(state, action) {
      state.expandedRowKeys = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGroupByLogs.pending, (state) => {
        state.loading = true
        state.error = null
        // state.totalPage = 1
        // state.totalRecord = 0
      })
      .addCase(getGroupByLogs.fulfilled, (state, action) => {
        const logs = action.payload.logs
        const total = action.payload.total
        state.loading = false
        state.logs = logs || []
        state.selectedGroupLogs = []
        state.totalPage = Math.ceil(total / 10) || 1
        state.totalRecord = total
      })
      .addCase(getGroupByLogs.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Unknown error'
        state.totalPage = 1
        state.totalRecord = 0
        state.logs = []
      })
      .addCase(getSelectedGroupByLogs.pending, (state) => {
        state.loadingSelectedGroupLogs = true
        // state.selectedGroupLogs = []
        // state.loading = true
        // state.error = null
        // state.totalPage = 1
        // state.totalRecord = 0
      })
      .addCase(getSelectedGroupByLogs.fulfilled, (state, action) => {
        const selectedLogs = action.payload.selectedLogs
        state.loadingSelectedGroupLogs = false
        // const total = action.payload.total
        // state.loading = false
        state.selectedGroupLogs.push(...(selectedLogs || []))
        // state.totalPage = Math.ceil(total / 10) || 1
        // state.totalRecord = total
      })
      .addCase(getSelectedGroupByLogs.rejected, (state, action) => {
        state.loadingSelectedGroupLogs = false
        // state.loading = false
        // state.error = action.error.message || 'Unknown error'
        // state.totalPage = 1
        // state.totalRecord = 0
        // state.selectedGroupLogs = []
      })
      .addCase(resolveRequestThunk.pending, (state) => {
        state.resolvedLoading = true
        state.error = null
        state.resolvedSuccess = false
      })
      .addCase(resolveRequestThunk.fulfilled, (state, action) => {
        state.resolvedLoading = false
        state.resolvedSuccess = true
      })
      .addCase(retryRequestThunk.pending, (state) => {
        state.retryLoading = true
        state.error = null
        state.retrySuccess = false
      })
      .addCase(retryRequestThunk.fulfilled, (state, action) => {
        state.retryLoading = false
        state.retrySuccess = true
      })
      .addCase(retryRequestThunk.rejected, (state, action) => {
        state.retryLoading = false
        state.retrySuccess = false
        state.error = action.error.message || 'Unknown error'
      })
      .addCase(getLogHistory.pending, (state) => {
        state.loadingHistory = true
        state.history = []
      })
      .addCase(getLogHistory.fulfilled, (state, action) => {
        const filter = action.payload.history
        state.history = filter
        state.loadingHistory = false
      })
      .addCase(getLogHistory.rejected, (state, action) => {
        state.history = []
        state.loadingHistory = false
      })
      .addCase(getCountryFiltersDetails.pending, (state) => {
        state.countries = []
      })
      .addCase(getCountryFiltersDetails.fulfilled, (state, action) => {
        const filter = action.payload.filter
        state.countries = filter
      })
      .addCase(getCountryFiltersDetails.rejected, (state, action) => {
        state.countries = []
      })
      .addCase(getCustomerPortalNameFiltersDetails.pending, (state) => {
        state.customer_portal_names = []
      })
      .addCase(getCustomerPortalNameFiltersDetails.fulfilled, (state, action) => {
        const filter = action.payload.filter
        state.customer_portal_names = filter
      })
      .addCase(getCustomerPortalNameFiltersDetails.rejected, (state, action) => {
        state.customer_portal_names = []
      })
      .addCase(getCustomerNameFiltersDetails.pending, (state) => {
        state.customer_names = []
      })
      .addCase(getCustomerNameFiltersDetails.fulfilled, (state, action) => {
        const filter = action.payload.filter
        state.customer_names = filter
      })
      .addCase(getCustomerNameFiltersDetails.rejected, (state, action) => {
        state.customer_names = []
      })
  },
})

export const selectCanGoPrevious = (state: RootState) => state.customLogs.pagination.currentPage > 1
export const selectCanGoNext = (state: RootState) =>
  state.customLogs.pagination.currentPage < state.customLogs.totalPage

export const {
  updateFilters,
  setPage,
  clearSearch,
  updatePagination,
  setLogStatus,
  clearSelectedLogGroups,
  setResolvedSelected,
  setExpandedRowKeys,
} = customLogSlice.actions
export default customLogSlice.reducer
